<template>
  <div v-if="shouldShowSteps" class="step-progress-container">
    <v-container class="pa-4">
    <v-row justify="center">
      <v-col cols="12" lg="10">
        <div class="step-progress">
          <!-- Brand name floats into the gap below the step bar without adding height -->
          <router-link to="/" class="step-brand-link">
            <BrandName class="step-brand" :name2-override="memorialName2Override" />
          </router-link>

          <div class="steps-container">
            <div
              v-for="(step, index) in visibleSteps"
              :key="step"
              class="step-item"
              :class="{
                'active': index + 1 === currentStep,
                'completed': index + 1 < currentStep,
                'clickable': isStepClickable(step, index + 1),
                'disabled': isStepDisabled(step)
              }"
              @click="handleStepClick(index + 1)"
            >
              <div class="step-circle">
                <v-icon v-if="index + 1 < currentStep" color="white" size="20">
                  mdi-check
                </v-icon>
                <span v-else class="step-number">{{ index + 1 }}</span>
              </div>
              <div class="step-label">{{ step }}</div>
              <!--<div v-if="index < visibleSteps.length - 1" class="step-connector"></div>-->
            </div>
          </div>

          <!-- Profile Menu (shown when logged in) -->
          <ProfileMenu v-if="isAuthenticated" class="profile-menu" />
        </div>
      </v-col>
    </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useMemorialStore } from '../stores/memorial'
import { useRouter } from 'vue-router'
import ProfileMenu from './ProfileMenu.vue'

const store = useMemorialStore()
const router = useRouter()

const currentStep = computed(() => store.currentStep)
const memorialName2Override = computed(() => store.personName ? '-' + store.personName : null)
const steps = computed(() => store.steps)

// Only show steps if not on Welcome page (step 0)
const shouldShowSteps = computed(() => currentStep.value > 0)

// Skip the first step (Welcome) when displaying
// Also skip the "Save" step (index 6) when editing an existing memorial
const visibleSteps = computed(() => {
  const allSteps = steps.value.slice(1)

  // If user is editing an existing memorial (has memorialUuid and is logged in), hide Save step
  const isEditingExisting = store.memorialUuid && store.isAccountCreated && store.isEmailVerified

  if (isEditingExisting) {
    return allSteps.filter(step => step !== 'Save')
  }

  return allSteps
})

// Check if user is authenticated
const isAuthenticated = computed(() => {
  return !!store.apiToken
})

// Check if a specific step should be clickable
const isStepClickable = (stepName, stepIndex) => {
  // Contacts and Share steps are only clickable if payment is completed
  if ((stepName === 'Contacts' || stepName === 'Share') && !store.paymentCompleted) {
    return false
  }

  // Users can only click steps they've reached or completed
  // Being logged in doesn't automatically grant free navigation anymore
  return stepIndex <= store.maxReachedStep
}

// Check if a step should be disabled (greyed out)
const isStepDisabled = (stepName) => {
  return (stepName === 'Contacts' || stepName === 'Share') && !store.paymentCompleted
}

const handleStepClick = (stepIndex) => {
  // Users can only navigate to steps they've reached
  // Step progression is now tracked per-memorial, not per-user
  const canNavigate = stepIndex <= store.maxReachedStep

  if (canNavigate) {
    // Navigate to appropriate route (router guard will handle unsaved changes)
    // Order: Welcome(0), Who(1), Photos(2), Message(3), Preview(4), Save(5), Payment(6), Contacts(7), Share(8)
    const routes = ['/', '/who', '/photos', '/message', '/preview', '/save', '/payment', '/contacts', '/share']

    // If editing existing memorial and Save step is hidden, adjust the stepIndex
    const isEditingExisting = store.memorialUuid && store.isAccountCreated && store.isEmailVerified
    let actualStepIndex = stepIndex

    if (isEditingExisting && stepIndex >= 5) {
      // Skip the Save step (index 5 in new order) by adding 1 to the index
      actualStepIndex = stepIndex + 1
    }

    // Prevent navigation to Contacts (index 7) and Share (index 8) if payment not completed
    if ((actualStepIndex === 7 || actualStepIndex === 8) && !store.paymentCompleted) {
      console.log('Payment must be completed before accessing Contacts or Share page')
      return
    }

    if (actualStepIndex < routes.length) {
      router.push(routes[actualStepIndex]).then(() => {
        // Only update current step if navigation was successful
        store.currentStep = actualStepIndex
      }).catch(() => {
        // Navigation was cancelled, don't update currentStep
      })
    }
  }
}

</script>

<style lang="scss" scoped>
@use "~@/styles/variables" as *;
@use "~@/styles/mixins" as *;
.step-progress-container {
  background: $white;
  border-bottom: $border-width solid $border;
}

.step-progress {
  position: relative;
}

.steps-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 20px;
  position: relative;
}

.profile-menu {
  position: absolute;
  top: 0;
  right: 0;
}

.step-brand-link {
  position: absolute;
  right: 0;
  bottom: -45px; /* hangs into the gap below the step bar border */
  text-decoration: none;
  z-index: 1;

  /* Align right edge with the content v-card (md-8 / lg-6 col) */
  @media (min-width: 960px) and (max-width: 1279px) {
    right: 17.67%;
  }

  @media (min-width: 1280px) {
    right: 21%;
  }
}

.step-brand-link :deep(.brand-name) {
  font-size: 1rem !important;
  letter-spacing: 0.15rem !important;
}

.profile-btn {
  opacity: 0.8;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  min-width: 80px;
  cursor: default;
  
  &.clickable {
    cursor: pointer;

    &:hover .step-circle {
      transform: scale(1.1);
    }
  }

  &.disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;

    .step-circle {
      background: var(--v-theme-neutral200);
      border-color: var(--v-theme-neutral200);
    }

    .step-label {
      color: var(--v-theme-neutral400);
    }
  }

  &.active {
    .step-circle {
      @include step-circle-active;
    }

    .step-label {
      color: $step-active;
      font-weight: 600;
    }
  }
  
  &.completed {
    .step-circle {
      @include step-circle-completed;
    }
    
    .step-label {
      color: $step-completed;
      font-weight: 600;
    }
    
    .step-connector {
      background: $step-completed;
    }
  }
}

.step-circle {
  @include step-circle-base;
  @include step-circle-inactive;
}

.step-label {
  font-size: 14px;
  font-weight: 500;
  color: $text-secondary;
  text-align: center;
  white-space: nowrap;
}

.step-connector {
  position: absolute;
  top: 20px;
  left: calc(100% + 10px);
  width: 40px;
  height: 3px;
  background: $step-inactive;
  z-index: -1;
}

.step-number {
  font-size: 16px;
  font-weight: bold;
}

// Responsive design
@media (max-width: 768px) {
  .steps-container {
    gap: 10px;
  }

  .step-item {
    min-width: 60px;
  }

  .step-circle {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }

  .step-label {
    font-size: 12px;
  }

  .step-connector {
    width: 20px;
    left: calc(100% + 5px);
    top: 16px;
  }

  .profile-menu {
    top: -5px;
    right: -10px;
  }
}

@include mobile {
  .steps-container {
    gap: 5px;
  }

  .step-item {
    min-width: 50px;
  }

  .step-circle {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }

  .step-label {
    font-size: 10px;
  }

  .step-connector {
    width: 15px;
    left: calc(100% + 2px);
    top: 14px;
  }

  .profile-menu {
    top: -10px;
    right: -15px;
  }

  .profile-btn {
    :deep(.v-icon) {
      font-size: 20px;
    }
  }
}
</style>
